import {
  AdminAuditLogEntryDto,
  AdminContentDetailDto,
  AdminContentListItemDto,
  AuthorSummaryDto,
  ContentDto,
  ContentVisibilityResponseDto,
} from '@/types/generated/schemas';

// Mirrors the backend content module DTOs/enums
// (see back-end/backend-monolith/src/content). The generated OpenAPI schemas are
// the source of truth; we re-alias them to shorter names and add the UI-facing
// filter constants the moderation screens need.

export type TContentType = ContentDto['type']; // 'video' | 'article' | 'post'
// 'withdrawn' is the creator-initiated soft delete (DELETE /content/:id). The
// generated schemas predate it, so it is added here until `generate:api-types`
// is re-run against the withdraw-aware backend.
export type TContentStatus = ContentDto['status'] | 'withdrawn';
export type TContentDomain =
  | 'mental'
  | 'spiritual'
  | 'vocational'
  | 'fun'
  | 'purpose'
  | 'social'
  | 'physical';

// Content carries multiple domains + subdomains. The generated schemas still expose
// the legacy single primaryDomain/subdomain until `generate:api-types` is re-run
// against the multi-domain backend, so we override those fields here (the Omit becomes
// a no-op once the spec is regenerated).
type ContentDomainFields = { domains: TContentDomain[]; subdomains: string[] };

// Same story for the withdrawal fields: the generated `status` enum has no
// 'withdrawn' member and the two withdrawal columns are missing entirely.
// These overrides become no-ops once the spec is regenerated.
type ContentWithdrawalFields = {
  status: TContentStatus;
  statusBeforeRemoval?: TContentStatus | null;
  withdrawnAt?: string | null;
  withdrawnByUserId?: string | null;
};

type ContentOverrides = 'primaryDomain' | 'subdomain' | 'status' | 'statusBeforeRemoval';

export type TContent = Omit<ContentDto, ContentOverrides> & ContentDomainFields & ContentWithdrawalFields;
export type TContentListItem = Omit<AdminContentListItemDto, ContentOverrides> &
  ContentDomainFields &
  ContentWithdrawalFields;
export type TContentDetail = Omit<AdminContentDetailDto, ContentOverrides> &
  ContentDomainFields &
  ContentWithdrawalFields;
export type TContentAuditLogEntry = AdminAuditLogEntryDto;
export type TContentAuthorSummary = AuthorSummaryDto;
export type TContentVisibility = ContentVisibilityResponseDto;

export const CONTENT_TYPES = ['video', 'article', 'post'] as const;

// Admin list status filter (note: `all` is a filter-only value, not a real status).
export const CONTENT_STATUS_FILTERS = ['published', 'removed', 'withdrawn', 'draft', 'all'] as const;
export type TContentStatusFilter = (typeof CONTENT_STATUS_FILTERS)[number];

// Admin list visibility filter.
export const CONTENT_VISIBILITY_FILTERS = ['all', 'public', 'org'] as const;
export type TContentVisibilityFilter = (typeof CONTENT_VISIBILITY_FILTERS)[number];

// Type-specific `details` payloads (subset we author from the backoffice).
export type TContentArticleSubsection = {
  _id: string;
  title?: string;
  text?: string;
  imageUrl?: string | null;
  order?: number;
};

export type TContentArticleSection = {
  _id: string;
  title?: string;
  text?: string;
  imageUrl?: string | null;
  order?: number;
  subsections?: TContentArticleSubsection[];
};

export type TContentDetailsPayload =
  | { body?: string } // post
  | { sections?: TContentArticleSection[] } // article
  | Record<string, unknown>; // video (not authored in this pass)

// Wrapper create payload — POST /admin/content/myomnia.
export type TCreateBrandedContentPayload = {
  type: TContentType;
  title?: string;
  description?: string;
  domains?: TContentDomain[];
  subdomains?: string[];
  details?: TContentDetailsPayload;
};

// Edit payload — PATCH /content/:id (type is immutable server-side, so omitted).
export type TUpdateContentPayload = {
  title?: string;
  description?: string;
  domains?: TContentDomain[];
  subdomains?: string[];
  details?: TContentDetailsPayload;
};

// Backend whitelist for the `sort` query param (`<field>:<asc|desc>`).
export const CONTENT_SORT_FIELDS = [
  'publishedAt',
  'createdAt',
  'updatedAt',
  'title',
  'type',
  'status',
] as const;
export type TContentSortField = (typeof CONTENT_SORT_FIELDS)[number];
